import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import api from "@/lib/api";

/**
 * LaunchBanner — Iteration 35 (settings-driven)
 * Reads `/launch-banner` (public endpoint) and renders only when
 * `is_active=true`. Falls back to silent hide on any error so a backend
 * blip never paints a broken banner. Dismiss is per-browser via
 * localStorage AND is invalidated whenever the admin changes the text or
 * the displayed code (so a fresh announcement re-surfaces for everyone).
 */
const STORAGE_KEY = "awtadk.launchBanner.dismissed.v2";

const LaunchBanner = () => {
  const [settings, setSettings] = useState(null);     // null=loading, {} once loaded
  const [dismissedFor, setDismissedFor] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/launch-banner");
        if (!cancelled) setSettings(data || {});
      } catch {
        if (!cancelled) setSettings({});      // silent hide on error
      }
    })();
    try {
      setDismissedFor(window.localStorage.getItem(STORAGE_KEY) || "");
    } catch {}
    return () => { cancelled = true; };
  }, []);

  if (!settings) return null;
  if (!settings.is_active) return null;

  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (path.startsWith("/admin") || path.startsWith("/checkout-success")) return null;

  const message = settings.banner_text || "";
  const code = (settings.displayed_code || "").trim();
  // Signature so a fresh announcement re-surfaces for users who dismissed
  // an older one. If admin edits text OR code, signature changes → re-show.
  const signature = `${message}::${code}`;
  if (dismissedFor === signature) return null;
  if (!message && !code) return null;       // nothing meaningful to show

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, signature); } catch {}
    setDismissedFor(signature);
  };

  return (
    <div
      data-testid="launch-banner"
      dir="rtl"
      className="relative z-50 bg-[#0A4D3C] text-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-[#C5A059] shrink-0" />
          <span className="truncate" data-testid="launch-banner-text">
            <strong className="text-[#C5A059] font-semibold">{message}</strong>
          </span>
          {code && (
            <span
              data-testid="launch-banner-code"
              className="ms-2 hidden sm:inline-flex items-center gap-1 rounded-full bg-[#C5A059] text-[#222222] text-[11px] font-bold px-2.5 py-0.5 font-mono tracking-widest"
              aria-label={`كود ${code}`}
              dir="ltr"
            >
              {code}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/services"
            data-testid="launch-banner-cta"
            className="rounded-full bg-[#C5A059] hover:bg-[#B08D45] text-[#222222] text-xs font-medium px-4 py-1.5 transition-colors"
          >
            احجز الآن
          </Link>
          <button
            type="button"
            aria-label="إغلاق الإعلان"
            data-testid="launch-banner-dismiss"
            onClick={dismiss}
            className="w-7 h-7 rounded-full hover:bg-white/10 grid place-items-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LaunchBanner;
